import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Refresh } from '@mui/icons-material';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';

// Features the backend meters. Free text is allowed too, but these are the ones that
// actually have enforcement behind them, so they are offered first.
const KNOWN_FEATURES = [
  { value: 'prediction_model', label: 'prediction_model — models a company may train' },
  { value: 'quantum_simulation', label: 'quantum_simulation — Quantum runs' },
  { value: 'MOVEO_UNIT', label: 'MOVEO_UNIT — analytics agent credits' },
];

/**
 * Edit the quota allowances on a subscription plan.
 *
 * These are per PLAN, not per company: changing one changes what every company on that
 * plan is allowed. That warning is on-screen, not just here.
 */
function PlanQuotasCard() {
  const { showSuccess, showError } = useToast();

  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState('');
  const [policies, setPolicies] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadingQuotas, setLoadingQuotas] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [newFeature, setNewFeature] = useState('');
  const [newAmount, setNewAmount] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoadingPlans(true);
      const result = await apiService.fetchSubscriptionPlans();
      if (result.success) {
        setPlans(result.data || []);
      } else {
        setError(result.error);
      }
      setLoadingPlans(false);
    };
    load();
  }, []);

  const fetchQuotas = useCallback(async (id) => {
    if (!id) {
      setPolicies([]);
      return;
    }
    setLoadingQuotas(true);
    setError(null);
    const result = await apiService.fetchPlanQuotas(id);
    if (result.success) {
      setPolicies(result.data?.quota_policies || []);
    } else {
      setError(result.error);
      setPolicies([]);
    }
    setLoadingQuotas(false);
  }, []);

  useEffect(() => {
    fetchQuotas(planId);
  }, [planId, fetchQuotas]);

  const selectedPlan = plans.find((p) => p.unique_id === planId);

  const save = async ({ quotas = [], remove = [] }) => {
    setSaving(true);
    const result = await apiService.updatePlanQuotas(planId, { quotas, remove });
    if (result.success) {
      setPolicies(result.data?.quota_policies || []);
      showSuccess('Plan quotas updated', 'Saved');
    } else {
      showError(result.error, 'Error');
    }
    setSaving(false);
    return result.success;
  };

  const handleAmountChange = (feature, value) => {
    setPolicies((prev) =>
      prev.map((p) =>
        p.feature === feature ? { ...p, feature_available_amount: value } : p,
      ),
    );
  };

  const handleAmountCommit = async (policy) => {
    const amount = parseInt(policy.feature_available_amount, 10);
    if (Number.isNaN(amount) || amount < 0) {
      showError('Allowance must be zero or more', 'Invalid value');
      fetchQuotas(planId);
      return;
    }
    await save({
      quotas: [{ feature: policy.feature, feature_available_amount: amount }],
    });
  };

  const handleRemove = async (feature) => {
    await save({ remove: [feature] });
  };

  const handleAdd = async () => {
    const feature = newFeature.trim();
    const amount = parseInt(newAmount, 10);
    if (!feature) {
      showError('Pick a feature first', 'Nothing to add');
      return;
    }
    if (Number.isNaN(amount) || amount < 0) {
      showError('Allowance must be zero or more', 'Invalid value');
      return;
    }
    const ok = await save({
      quotas: [{ feature, feature_available_amount: amount }],
    });
    if (ok) {
      setNewFeature('');
      setNewAmount('');
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="h6">Plan quotas</Typography>
          <Tooltip title="Reload">
            <span>
              <IconButton
                onClick={() => fetchQuotas(planId)}
                disabled={!planId || loadingQuotas}
                size="small"
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Alert severity="warning" sx={{ mb: 3 }}>
          These allowances apply to <strong>every company on the plan</strong>, not to one
          customer. To give a single customer more, move them to a different plan.
        </Alert>

        {loadingPlans ? (
          <CircularProgress size={24} />
        ) : (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="plan-quota-plan-label">Plan</InputLabel>
            <Select
              labelId="plan-quota-plan-label"
              value={planId}
              label="Plan"
              onChange={(e) => setPlanId(e.target.value)}
            >
              <MenuItem value="">
                <em>Select a plan</em>
              </MenuItem>
              {plans.map((plan) => (
                <MenuItem key={plan.unique_id} value={plan.unique_id}>
                  {plan.name}
                  {plan.subtitle ? ` — ${plan.subtitle}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!planId && !loadingPlans && (
          <Typography variant="body2" color="text.secondary">
            Pick a plan to see and edit what it grants.
          </Typography>
        )}

        {planId && loadingQuotas && <CircularProgress size={24} />}

        {planId && !loadingQuotas && (
          <>
            {policies.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>{selectedPlan?.name || 'This plan'}</strong> has no quotas — every
                feature is <strong>unmetered</strong>. That is how unlimited is expressed;
                it does not mean zero.
              </Alert>
            ) : (
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Feature</TableCell>
                    <TableCell width={160}>Allowance</TableCell>
                    <TableCell width={140}>Resets every</TableCell>
                    <TableCell width={60} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {policies.map((policy) => (
                    <TableRow key={policy.unique_id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {policy.feature}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={policy.feature_available_amount}
                          disabled={saving}
                          onChange={(e) =>
                            handleAmountChange(policy.feature, e.target.value)
                          }
                          onBlur={() => handleAmountCommit(policy)}
                          inputProps={{ min: 0 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={`${policy.reset_amount} ${policy.reset_unit}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Remove — makes this feature unmetered">
                          <span>
                            <IconButton
                              size="small"
                              disabled={saving}
                              onClick={() => handleRemove(policy.feature)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 280 }} size="small">
                <InputLabel id="new-feature-label">Add feature</InputLabel>
                <Select
                  labelId="new-feature-label"
                  value={newFeature}
                  label="Add feature"
                  onChange={(e) => setNewFeature(e.target.value)}
                  disabled={saving}
                >
                  {KNOWN_FEATURES.filter(
                    (f) => !policies.some((p) => p.feature === f.value),
                  ).map((f) => (
                    <MenuItem key={f.value} value={f.value}>
                      {f.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label="Allowance"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                disabled={saving}
                inputProps={{ min: 0 }}
                sx={{ width: 140 }}
              />
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAdd}
                disabled={saving || !newFeature}
              >
                Add
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default PlanQuotasCard;
